import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Switch,
  Text,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';

import BranchIcon from 'components/Icons/BranchIcon';
import { Scenario } from 'models/response/scenario.interface';
import { groupByFilterChange } from 'redux/PlanPageSlice';
import {
  getAllScenarios,
  getBaseScenario,
  getCompareScenario,
  getToggleScenario,
  selectBaseScenario,
  selectCompareScenario,
  toggleCompareScenario,
} from 'redux/ScenarioSlice';
import { getPlanPageThemeColors } from 'redux/ThemeSlice';
import { AppDispatch } from 'utils/GlobalHelpers';

import {
  PLAN_ACCENT_BLUE,
  PLAN_COMPARE_SCENARIO_DOT,
  PLAN_REVAMP_RADIUS_SM,
  PLAN_TEXT,
} from '../planPageRevampUi';
import { PlanToolbarChevronDownIcon } from '../icons';

interface ScenarioFieldProperties {
  label: string;
  dotColor: string;
  selectedScenario: Scenario | null;
  scenarios: Scenario[];
  excludedScenarioId?: number;
  onSelect: (scenario: Scenario) => void;
}

const PlanScenarioSelectField = memo(
  ({
    label,
    dotColor,
    selectedScenario,
    scenarios,
    excludedScenarioId,
    onSelect,
  }: ScenarioFieldProperties) => {
    const themeColors = useSelector(getPlanPageThemeColors);

    const options = useMemo(
      () => scenarios.filter((scenario) => scenario.id !== excludedScenarioId),
      [excludedScenarioId, scenarios],
    );

    return (
      <Box>
        <Text {...PLAN_TEXT.fieldLabel} color={themeColors.textSecondary} mb={2} fontWeight={600}>
          {label}
        </Text>
        <Menu matchWidth>
          <MenuButton
            as={Button}
            w='100%'
            h='40px'
            minH='40px'
            px={3}
            py={0}
            {...PLAN_TEXT.body}
            border='1px solid'
            borderColor={themeColors.border}
            borderRadius={PLAN_REVAMP_RADIUS_SM}
            bg={themeColors.surface}
            color={themeColors.textDark}
            display='inline-flex'
            alignItems='center'
            flexWrap='nowrap'
            boxShadow='none'
            _hover={{ bg: themeColors.surface, borderColor: themeColors.primary }}
            _expanded={{ bg: themeColors.surface, borderColor: themeColors.primary }}
            _focus={{ boxShadow: 'none' }}
          >
            <Flex
              alignItems='center'
              justifyContent='space-between'
              gap={2}
              flexWrap='nowrap'
              w='100%'
              minW={0}
            >
              <Flex alignItems='center' gap={2} minW={0} flex='1' flexWrap='nowrap'>
                <Box w='8px' h='8px' borderRadius='full' bg={dotColor} flexShrink={0} />
                <Text
                  as='span'
                  {...PLAN_TEXT.body}
                  noOfLines={1}
                  textAlign='left'
                  whiteSpace='nowrap'
                  minW={0}
                  flex='1'
                >
                  {selectedScenario?.name ?? 'Select scenario'}
                </Text>
              </Flex>
              <Box flexShrink={0} display='flex' alignItems='center'>
                <PlanToolbarChevronDownIcon width={10} height={6} color={themeColors.primary} />
              </Box>
            </Flex>
          </MenuButton>
          <MenuList zIndex={1600} py={1} borderRadius={PLAN_REVAMP_RADIUS_SM} boxShadow='md'>
            {options.map((scenario) => (
              <MenuItem
                key={scenario.id}
                {...(selectedScenario?.id === scenario.id
                  ? PLAN_TEXT.bodySemibold
                  : PLAN_TEXT.body)}
                color={
                  selectedScenario?.id === scenario.id ? themeColors.primary : themeColors.textDark
                }
                bg={selectedScenario?.id === scenario.id ? themeColors.activeBg : 'transparent'}
                _hover={{ bg: themeColors.activeBg }}
                onClick={() => onSelect(scenario)}
              >
                {scenario.name}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
      </Box>
    );
  },
);

PlanScenarioSelectField.displayName = 'PlanScenarioSelectField';

interface Properties {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  refetchRequest: () => void;
  persistScenario: (scenario: Scenario) => void;
}

const PlanScenarioComparisonPopover = ({
  isOpen,
  onOpen,
  onClose,
  refetchRequest,
  persistScenario,
}: Properties) => {
  const dispatch: AppDispatch = useDispatch();
  const themeColors = useSelector(getPlanPageThemeColors);
  const scenarios = useSelector(getAllScenarios);
  const baseScenario = useSelector(getBaseScenario);
  const compareScenario = useSelector(getCompareScenario);
  const compareEnabled = useSelector(getToggleScenario);

  const [draftBaseScenario, setDraftBaseScenario] = useState<Scenario | null>(null);
  const [draftCompareEnabled, setDraftCompareEnabled] = useState(false);
  const [draftCompareScenario, setDraftCompareScenario] = useState<Scenario | null>(null);

  const canCompare = (scenarios?.length ?? 0) > 1;

  const syncDraftFromStore = useCallback(() => {
    setDraftBaseScenario(baseScenario ?? null);
    setDraftCompareEnabled(Boolean(compareEnabled));
    setDraftCompareScenario(compareScenario ?? null);
  }, [baseScenario, compareEnabled, compareScenario]);

  useEffect(() => {
    if (isOpen) {
      syncDraftFromStore();
    }
  }, [isOpen, syncDraftFromStore]);

  const pickDefaultCompareScenario = useCallback(
    (base: Scenario | null) => {
      if (!base) return null;
      return scenarios.find((scenario: Scenario) => scenario.id !== base.id) ?? null;
    },
    [scenarios],
  );

  const onDraftCompareToggle = useCallback(
    (enabled: boolean) => {
      setDraftCompareEnabled(enabled);
      if (enabled) {
        setDraftCompareScenario(
          (current) => current ?? pickDefaultCompareScenario(draftBaseScenario),
        );
      } else {
        setDraftCompareScenario(null);
      }
    },
    [draftBaseScenario, pickDefaultCompareScenario],
  );

  const onDraftBaseChange = useCallback(
    (scenario: Scenario) => {
      setDraftBaseScenario(scenario);
      if (draftCompareScenario?.id === scenario.id) {
        setDraftCompareScenario(pickDefaultCompareScenario(scenario));
      }
    },
    [draftCompareScenario?.id, pickDefaultCompareScenario],
  );

  const onCancel = useCallback(() => {
    syncDraftFromStore();
    onClose();
  }, [onClose, syncDraftFromStore]);

  const onApply = useCallback(() => {
    if (!draftBaseScenario) return;

    persistScenario(draftBaseScenario);
    dispatch(selectBaseScenario(draftBaseScenario));

    if (draftCompareEnabled && canCompare && draftCompareScenario) {
      dispatch(toggleCompareScenario(true));
      dispatch(groupByFilterChange('Total'));
      dispatch(selectCompareScenario(draftCompareScenario));
    } else {
      dispatch(toggleCompareScenario(false));
    }

    refetchRequest();
    onClose();
  }, [
    canCompare,
    dispatch,
    draftBaseScenario,
    draftCompareEnabled,
    draftCompareScenario,
    onClose,
    persistScenario,
    refetchRequest,
  ]);

  const triggerLabel = useMemo(() => {
    if (compareEnabled && compareScenario?.name) {
      return `${baseScenario?.name ?? 'Scenario'} vs ${compareScenario.name}`;
    }
    return baseScenario?.name ?? 'Scenario';
  }, [baseScenario?.name, compareEnabled, compareScenario?.name]);

  if (!canCompare || !baseScenario) {
    return null;
  }

  return (
    <Popover placement='bottom-end' isOpen={isOpen} onOpen={onOpen} onClose={onCancel} closeOnBlur>
      <PopoverTrigger>
        <Flex
          as='button'
          type='button'
          alignItems='center'
          gap={2}
          px={3}
          h='40px'
          minH='40px'
          w='100%'
          minW={0}
          maxW='240px'
          flexWrap='nowrap'
          bg='transparent'
          border='none'
          cursor='pointer'
          _hover={{ bg: themeColors.activeBg }}
          _focus={{ outline: 'none', boxShadow: 'none' }}
        >
          <Flex alignItems='center' gap={2} minW={0} flex='1' flexWrap='nowrap'>
            <Box w='8px' h='8px' borderRadius='full' bg={PLAN_ACCENT_BLUE} flexShrink={0} />
            {compareEnabled && compareScenario && (
              <Box
                w='8px'
                h='8px'
                borderRadius='full'
                bg={PLAN_COMPARE_SCENARIO_DOT}
                flexShrink={0}
              />
            )}
            <Text
              {...PLAN_TEXT.body}
              color={themeColors.textDark}
              noOfLines={1}
              minW={0}
              flex='1'
              textAlign='left'
              whiteSpace='nowrap'
            >
              {triggerLabel}
            </Text>
          </Flex>
          <Box flexShrink={0} display='flex' alignItems='center'>
            <PlanToolbarChevronDownIcon width={10} height={6} color={themeColors.primary} />
          </Box>
        </Flex>
      </PopoverTrigger>

      <PopoverContent
        w='320px'
        border='none'
        borderRadius='14px'
        bg={themeColors.surface}
        zIndex={1600}
        boxShadow='0px 8px 24px rgba(0, 0, 0, 0.12)'
        _focus={{ outline: 'none', boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)' }}
      >
        <PopoverBody p={5} bg={themeColors.surface}>
          <Text {...PLAN_TEXT.modalTitle} color={themeColors.textDark}>
            Scenario Comparison
          </Text>
          <Text {...PLAN_TEXT.modalSubtitle} color={themeColors.textSecondary} mt={1} mb={4}>
            Choose which scenarios to compare in variance cards and KPIs
          </Text>

          <Flex direction='column' gap={4}>
            <PlanScenarioSelectField
              label='Base Scenario'
              dotColor={PLAN_ACCENT_BLUE}
              selectedScenario={draftBaseScenario}
              scenarios={scenarios}
              excludedScenarioId={draftCompareEnabled ? draftCompareScenario?.id : undefined}
              onSelect={onDraftBaseChange}
            />

            <Flex
              alignItems='center'
              justifyContent='space-between'
              gap={3}
              px={3}
              h='44px'
              borderRadius={PLAN_REVAMP_RADIUS_SM}
              bg={themeColors.activeBg}
            >
              <Flex alignItems='center' gap={2} width='max-content'>
                <BranchIcon width={6} height={6} />
                <Text {...PLAN_TEXT.bodyMedium} color={themeColors.textDark}>
                  Compare
                </Text>
              </Flex>
              <Switch
                size='sm'
                colorScheme='purple'
                isChecked={draftCompareEnabled}
                onChange={(event) => onDraftCompareToggle(event.target.checked)}
              />
            </Flex>

            {draftCompareEnabled && (
              <PlanScenarioSelectField
                label='Compare Against'
                dotColor={PLAN_COMPARE_SCENARIO_DOT}
                selectedScenario={draftCompareScenario}
                scenarios={scenarios}
                excludedScenarioId={draftBaseScenario?.id}
                onSelect={setDraftCompareScenario}
              />
            )}
          </Flex>

          <Flex gap={3} mt={6}>
            <Button
              flex='1'
              h='40px'
              {...PLAN_TEXT.button}
              border='1px solid'
              borderColor={themeColors.border}
              borderRadius={PLAN_REVAMP_RADIUS_SM}
              bg={themeColors.surface}
              color={themeColors.textDark}
              boxShadow='none'
              _hover={{ bg: themeColors.activeBg }}
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              flex='1'
              h='40px'
              {...PLAN_TEXT.button}
              borderRadius={PLAN_REVAMP_RADIUS_SM}
              bg={themeColors.primary}
              color={themeColors.white}
              boxShadow='none'
              _hover={{ bg: themeColors.primaryHover }}
              _active={{ bg: themeColors.primaryPressed }}
              onClick={onApply}
              isDisabled={!draftBaseScenario || (draftCompareEnabled && !draftCompareScenario)}
            >
              Apply
            </Button>
          </Flex>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default memo(PlanScenarioComparisonPopover);
